import { useState } from 'react';
import HealthBackgroundService from '../Services/HealthBackgroundService';
import { useActivePatient } from './ActivePatientContext';
import type {
  CreateAllergyRequest,
  UpdateAllergyRequest,
  AddAllergyNoteRequest,
  AddAllergyReactionRequest,
  CreateFamilyMemberRequest,
  AddRelativeConditionRequest,
  HereditaryBulkUpdateRequest,
  HealthBackgroundResponse,
} from '../../Types/schema-helpers';

const serviceInstance = new HealthBackgroundService();

export const useHealthBackgroundActions = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { activePatient, isSelfViewing, updateActiveHealthBackground } = useActivePatient();

  // Resolve target username: undefined targets /me, string targets specific patient
  const targetUsername = isSelfViewing ? undefined : activePatient?.patient?.username;

  // --- ALLERGIES ---

  const addAllergy = async (payload: CreateAllergyRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.addPatientAllergy(targetUsername as string, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to add allergy.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateAllergy = async (allergyId: string, payload: UpdateAllergyRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.updatePatientAllergy(targetUsername as string, allergyId, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to update allergy.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addAllergyNote = async (allergyId: string, payload: AddAllergyNoteRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.addPatientAllergyNote(targetUsername as string, allergyId, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to add note.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addAllergyReaction = async (allergyId: string, payload: AddAllergyReactionRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.addPatientAllergyReaction(targetUsername as string, allergyId, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to add reaction.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAllergy = async (allergyId: string) => {
    setLoading(true);
    setError(null);
    try {
      await serviceInstance.deletePatientAllergy(targetUsername as string, allergyId);
      // Safe filter with fallback array check
      if (activePatient?.health_background) {
        const existingAllergies = activePatient.health_background.allergies ?? []; // 👈 Guard against undefined
        const updatedBg: HealthBackgroundResponse = {
          ...activePatient.health_background,
          allergies: existingAllergies.filter((a) => a.allergy_id !== allergyId),
        };
        updateActiveHealthBackground(updatedBg);
      }
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to delete allergy.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --- HEREDITARY ---

  const addFamilyMember = async (payload: CreateFamilyMemberRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.addPatientFamilyMember(targetUsername as string, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to add family member.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addRelativeCondition = async (memberId: string, payload: AddRelativeConditionRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.addPatientRelativeCondition(targetUsername as string, memberId, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to add condition.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteFamilyMember = async (memberId: string) => {
    setLoading(true);
    setError(null);
    try {
      await serviceInstance.deletePatientFamilyMember(targetUsername as string, memberId);
      // Remove deleted member locally from active state
      if (activePatient?.health_background?.hereditary) {
        const h = activePatient.health_background.hereditary;
        const updatedBg = {
          ...activePatient.health_background,
          hereditary: {
            ...h,
            maternal: (h.maternal ?? []).filter((m) => m.id !== memberId),
            paternal: (h.paternal ?? []).filter((m) => m.id !== memberId),
            siblings: (h.siblings ?? []).filter((m) => m.id !== memberId),
          },
        };
        updateActiveHealthBackground(updatedBg);
      }
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to delete family member.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateHereditary = async (payload: HereditaryBulkUpdateRequest) => {
    setLoading(true);
    setError(null);
    try {
      const res = await serviceInstance.bulkUpdatePatientHereditary(targetUsername as string, payload);
      if (res) {
        updateActiveHealthBackground(res);
      }
      return res;
    } catch (err: any) {
      setError(err?.message || 'Failed to bulk update hereditary history.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    addAllergy,
    updateAllergy,
    addAllergyNote,
    addAllergyReaction,
    deleteAllergy,
    addFamilyMember,
    addRelativeCondition,
    deleteFamilyMember,
    bulkUpdateHereditary,
  };
};